import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  ApolloQueryResult,
  OperationVariables,
  useMutation,
} from '@apollo/client';
import { Card, Input, Select } from '@avila-tek/ui/src';
import { Button } from '@avila-tek/ui/src/Button';
import { useRouter } from 'next/router';
import { useNotify, useUser } from '../../hooks';
import { ClientGoals, Company, Criteria, Goal, Period } from '../../models';
import { CREATE_PERIOD, UPDATE_COMPANY_DETAILS } from '../../graphql/mutation';
import { FormHeader } from '../shared/Forms';
import { Alert } from '../shared';
import { validatePeriodForm } from '../../lib/formValidations';
import { removeProperties } from '../../lib/removeProperties';
import { updateGoalPeriod } from '../../lib/utils';
import { UNIT_TIME_SELECTABLES } from '../../constants/constants';
import CriteriaInput from '../goal/CriteriaInput';

interface PeriodFormProps {
  companyId: string;
  goalId?: string;
  period?: Period;
  refetch?: (variables?: Partial<OperationVariables>) => Promise<
    ApolloQueryResult<{
      clientGoals: ClientGoals[];
    }>
  >;
}

const initialCriteriasState = [
  {
    _id: uuidv4(),
    lowerRange: 100,
    upperRange: 100,
    reward: null,
  },
];

function PeriodForm({ companyId, goalId, period, refetch }: PeriodFormProps) {
  const notify = useNotify();
  const router = useRouter();
  const [user] = useUser();

  const [firstInfo, setFirstInfo] = React.useState(false);
  const [expandedIntervals, setExpandedIntervals] = React.useState(false);
  const [formState, setFormState] = React.useState<Partial<Period>>({
    name: '',
    target: 0,
    criterias: initialCriteriasState,
    startDate: '',
    endDate: '',
    unitTime: '',
  });

  const [createPeriod] = useMutation<{
    createPeriod: Period;
  }>(CREATE_PERIOD);

  const [updateCompanyDetailsMutation] = useMutation<{
    updateCompanyDetails: Company;
  }>(UPDATE_COMPANY_DETAILS);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // string to number in rewards criterias
    const newCriterias = formState.criterias.map((criteria: Criteria) => ({
      ...criteria,
      reward: Number(criteria.reward),
    }));

    const state = {
      ...formState,
      target: Number(formState.target),
      criterias: newCriterias,
    };

    try {
      // validate period
      const error = validatePeriodForm({ ...state });
      if (error) return notify(error, 'error');
      if (period) {
        updatePeriod(state as Period);
      } else {
        createPeriodFunction(state as Period);
      }
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  const createPeriodFunction = async (periodState: Period) => {
    try {
      const { data } = await createPeriod({
        variables: {
          data: {
            companyId,
            goalId,
            name: periodState.name,
            target: periodState.target,
            unitTime: periodState.unitTime,
            startDate: periodState.startDate,
            endDate: periodState.endDate,
            criterias: removeProperties(periodState.criterias, ['_id']),
          },
        },
      });
      if (data?.createPeriod?.companyDetails) {
        notify('Período creado', 'success');
        // El id del período recien creado se usara para ir a la pantalla de asignar período

        // La meta que contiene al ultimo periodo creado
        const goalUpdated = data?.createPeriod?.companyDetails.goals.find(
          (goalItem: Goal) => goalItem._id === goalId
        );
        // El id del último período creado
        const lastPeriodId =
          goalUpdated.periods[goalUpdated.periods.length - 1]._id;

        router.push(`/app/periods/assignPeriod/${lastPeriodId}`);
      }
    } catch (error) {
      console.log(error);
      notify(error.message, 'error');
    }
  };

  const updatePeriod = async (periodState: Period) => {
    const goalsWithUpdatedPeriod = updateGoalPeriod(
      user.company as Company,
      goalId,
      periodState,
      period?._id as string
    );
    try {
      const { data } = await updateCompanyDetailsMutation({
        variables: {
          data: {
            companyId: (user.company as Company)?._id,
            companyDetails: {
              ...removeProperties(user.company as Company, [
                '__typename',
                'active',
              ])?.companyDetails,
              _id: undefined,
              goals: removeProperties(goalsWithUpdatedPeriod, [
                '__typename',
                'active',
              ]),
            },
          },
        },
      });
      if (data?.updateCompanyDetails?._id) {
        notify('Período actualizado', 'success');
      }
    } catch (error) {
      console.log(error);
      notify(error.message, 'error');
    } finally {
      refetch?.();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleAddNewCriteria = () => {
    if (firstInfo === false) {
      notify(
        'Las recompensas creadas anteriormente no se ajustarán a este nuevo criterio',
        'warning'
      );
      setFirstInfo(true);
    }
    setFormState((prevState) => {
      const { criterias } = prevState;
      const newCriteria = {
        lowerRange: null,
        upperRange: null,
        reward: null,
      };

      if (expandedIntervals) {
        const updatedCriterias = [
          ...criterias.slice(0, -1),
          criterias[criterias.length - 1],
          newCriteria,
        ];
        return {
          ...prevState,
          criterias: updatedCriterias,
        };
      }

      return {
        ...prevState,
        criterias: [
          ...criterias.slice(0, -1),
          newCriteria,
          criterias[criterias.length - 1],
        ],
      };
    });
  };

  const restardCriteriasIntervals = () => {
    if (!expandedIntervals) {
      const firstCriteria = [
        {
          lowerRange: 0,
          upperRange: null,
          reward: null,
        },
      ];
      setFormState((prevState) => ({
        ...prevState,
        criterias: firstCriteria,
      }));
    } else {
      setFormState((prevState) => ({
        ...prevState,
        criterias: initialCriteriasState,
      }));
    }
    setExpandedIntervals((prevIntervals) => !prevIntervals);
  };

  // Siempre el primer intervalo de criterios tendra en el lowerRange 0 (cada que se agregue un nuevo intervalo)
  React.useEffect(() => {
    setFormState((prevState) => ({
      ...prevState,
      criterias: prevState.criterias.map((criteria, index) => {
        // Si es el primer intervalo, seteo el lowerRange a 0
        if (prevState.criterias.length === 1) {
          return {
            ...criteria,
          };
        }
        if (index === 0) {
          return {
            ...criteria,
            lowerRange: 0,
          };
        }
        return criteria;
      }),
    }));
  }, [formState.criterias.length]);

  React.useEffect(() => {
    if (period) {
      const lastCriteria = period.criterias[period.criterias.length - 1];
      if (lastCriteria.lowerRange !== 100 && lastCriteria.upperRange !== 100) {
        setExpandedIntervals(true);
      }
      setFormState({
        name: period.name,
        target: period.target,
        criterias:
          period.criterias.length > 0
            ? period.criterias
            : initialCriteriasState,
        startDate: new Date(period.startDate).toISOString().split('T')[0],
        endDate: new Date(period.endDate).toISOString().split('T')[0],
        unitTime: period.unitTime,
      });
    }
  }, [period]);

  const handleRemoveCriteria = (index: number) => {
    setFormState((prevState) => ({
      ...prevState,
      criterias: prevState.criterias.filter((_, i) => i !== index),
    }));
  };

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      <Card>
        <FormHeader backBtnText="" title="Período de meta" />
        <Card.Body>
          <div className="w-full flex flex-col gap-5">
            <div className="w-full flex gap-8 flex-col md:flex-row">
              <div className="w-full flex gap-8 flex-col">
                <div className="flex flex-col md:flex-row gap-5">
                  <div className="w-full">
                    <Input
                      name="name"
                      value={formState.name ?? ''}
                      label="Nombre"
                      type="text"
                      placeholder="Ingrese un nombre"
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="w-full">
                    <Input
                      name="target"
                      value={formState?.target ?? ''}
                      label="Meta"
                      type="number"
                      placeholder="Ingrese un valor para la meta"
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="w-full">
                    <Select
                      name="unitTime"
                      value={formState?.unitTime ?? ''}
                      label="Unidad de tiempo"
                      onChange={handleChangeSelect}
                    >
                      {UNIT_TIME_SELECTABLES.map((unitTime) => (
                        <option key={unitTime.value} value={unitTime.value}>
                          {unitTime.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-5">
                  <div className="w-full">
                    <Input
                      name="startDate"
                      value={formState?.startDate ?? ''}
                      label="Fecha de inicio"
                      type="date"
                      onChange={handleChange}
                    />
                  </div>
                  <div className="w-full">
                    <Input
                      name="endDate"
                      value={formState?.endDate ?? ''}
                      label="Fecha de fin"
                      type="date"
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-row space-x-10">
            <h3 className="font-semibold text-purple-300">
              Recompensa de acuerdo al cumplimiento de la meta
            </h3>
            <div className="flex flex-row justify-center gap-4">
              <input
                type="checkbox"
                checked={expandedIntervals}
                onChange={restardCriteriasIntervals}
                className="w-4 h-4 mt-1 cursor-pointer bg-white rounded border-purple-200 focus:ring-purple-300 focus:ring-2"
              />
              <label>+ 100% detallado</label>
            </div>
          </div>
          <Alert
            title="Intervalos de cumplimiento"
            text="A continuacion se colocan los intervalos de cumplimeinto de la métrica, dependiendo del procentaje de cumplimiento de la meta puede otorgar cantidad de puntos a sus beneficiarios"
            show
          />
          {formState.criterias?.map((criteria: Criteria, index: number) => (
            <div key={criteria?._id ?? index} className="w-full">
              <CriteriaInput
                key={criteria?._id ?? index}
                formState={formState}
                setFormState={setFormState}
                criteria={criteria}
                index={index}
                expandedIntervals={expandedIntervals}
                handleRemoveCriteria={handleRemoveCriteria}
              />
            </div>
          ))}
          <div className="flex justify-items-end items-center w-1/4">
            <Button
              onClick={handleAddNewCriteria}
              type="button"
              className="bg-transparent text-purple-800"
            >
              + Agregar nuevo intervalo
            </Button>
          </div>
        </Card.Body>
      </Card>
      <div className="flex md:w-1/3 md:ml-auto">
        <Button type="submit">Guardar</Button>
      </div>
    </form>
  );
}

export default PeriodForm;
